/**
 * Form Validation Utilities
 * Consolidated validation functions for all form types across the application.
 * This utility merges and standardizes validation from the previous validation.js
 * and policyValidators.js files.
 */

import { isValidDate } from './validation';

/**
 * Core Validation Functions
 * Basic validators that can be combined for more complex validations
 */

/**
 * Validates if a value is not empty
 * @param {string|array|object} value - The value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isNotEmpty = (value) => {
  if (value === null || value === undefined) return false;
  
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  
  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }
  
  return true;
};

/**
 * Validates an email address
 * @param {string} email - The email to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

/**
 * Validates a phone number with (XXX) XXX-XXXX format
 * @param {string} phone - The phone number to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidPhone = (phone) => {
  if (!phone) return false;
  // First remove any non-digit characters for raw validation
  const rawPhone = phone.replace(/\D/g, '');
  // Check if we have 10 digits (standard US phone)
  if (rawPhone.length !== 10) return false;
  
  // If it's in formatted form, check the format
  if (phone.includes('(')) {
    const re = /^\(\d{3}\) \d{3}-\d{4}$/;
    return re.test(phone);
  }
  
  return true;
};

/**
 * Validates a zip code (5 digits or 5+4 format)
 * @param {string} zipCode - The zip code to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidZipCode = (zipCode) => {
  if (!zipCode) return false;
  const re = /^\d{5}(-\d{4})?$/;
  return re.test(zipCode);
};

/**
 * Validates that a string is at least a minimum length
 * @param {string} value - The string to validate
 * @param {number} minLength - The minimum length required
 * @returns {boolean} - True if valid, false otherwise
 */
export const isMinLength = (value, minLength) => {
  if (!value || typeof value !== 'string') return false;
  return value.length >= minLength;
};

/**
 * Validates that a string does not exceed a maximum length
 * @param {string} value - The string to validate
 * @param {number} maxLength - The maximum length allowed
 * @returns {boolean} - True if valid, false otherwise
 */
export const isMaxLength = (value, maxLength) => {
  if (!value || typeof value !== 'string') return true; // Empty is valid for max length
  return value.length <= maxLength;
};

/**
 * Validates that a number is within a range
 * @param {number} value - The number to validate
 * @param {number} min - The minimum value
 * @param {number} max - The maximum value
 * @returns {boolean} - True if valid, false otherwise
 */
export const isInRange = (value, min, max) => {
  if (value === null || value === undefined) return false;
  const num = Number(value);
  if (isNaN(num)) return false;
  return num >= min && num <= max;
};

/**
 * Validates that a date is in the future
 * @param {string} date - The date string to validate (YYYY-MM-DD)
 * @returns {boolean} - True if valid, false otherwise
 */
export const isFutureDate = (date) => {
  if (!isValidDate(date)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  return d >= today;
};

/**
 * Validates that a date is in the past
 * @param {string} date - The date string to validate (YYYY-MM-DD)
 * @returns {boolean} - True if valid, false otherwise
 */
export const isPastDate = (date) => {
  if (!isValidDate(date)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  return d < today;
};

/**
 * Validates that a URL is valid
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidUrl = (url) => {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Policy Specific Validations
 * Specialized validation functions for insurance policy forms
 */

/**
 * Validates that a date field is not empty and is in the future
 * @param {string} date - Date string to validate
 * @returns {string} Error message if invalid, empty string if valid
 */
export const validateEffectiveDate = (date) => {
  if (!date) {
    return 'Effective date is required';
  }
  
  if (!isValidDate(date)) {
    return 'Please enter a valid date';
  }
  
  if (!isFutureDate(date)) {
    return 'Effective date must be today or in the future';
  }
  
  return '';
};

/**
 * Validates that at least one change option is selected
 * @param {Object} selectedOptions - Object where keys are option names and values are booleans
 * @returns {string} Error message if invalid, empty string if valid
 */
export const validateChangeSelection = (selectedOptions) => {
  if (!Object.values(selectedOptions).some(isSelected => isSelected)) {
    return 'Please select at least one change option';
  }
  return '';
};

/**
 * Validates if a coverage amount falls within valid range
 * @param {number} amount - Amount to validate
 * @param {number} min - Minimum valid amount
 * @param {number} max - Maximum valid amount
 * @returns {string} Error message if invalid, empty string if valid
 */
export const validateCoverageAmount = (amount, min, max) => {
  if (!amount && amount !== 0) {
    return 'Coverage amount is required';
  }
  
  if (isNaN(amount)) {
    return 'Coverage amount must be a number';
  }
  
  if (amount < min || amount > max) {
    return `Coverage amount must be between $${min.toLocaleString()} and $${max.toLocaleString()}`;
  }
  
  return '';
};

/**
 * Validates if a deductible amount is valid
 * @param {number} amount - Deductible amount to validate
 * @param {Array} validOptions - Array of valid deductible options
 * @returns {string} Error message if invalid, empty string if valid
 */
export const validateDeductible = (amount, validOptions) => {
  if (!amount && amount !== 0) {
    return 'Deductible amount is required';
  }
  
  if (isNaN(amount)) {
    return 'Deductible amount must be a number';
  }
  
  if (!validOptions.includes(Number(amount))) {
    return `Deductible must be one of the following values: ${validOptions.map(v => '$' + v.toLocaleString()).join(', ')}`;
  }
  
  return '';
};

/**
 * Creates a validation object for a form field
 * @param {any} value - The value to validate
 * @param {string} errorMessage - The error message to display
 * @param {function} validationFn - The validation function to use
 * @returns {object} - An object with error and errorMessage properties
 */
export const validateField = (value, errorMessage, validationFn) => {
  const isValid = validationFn(value);
  return {
    error: !isValid,
    errorMessage: isValid ? '' : errorMessage
  };
};

/**
 * Combines multiple validators into a single validator
 * @param {...function} fns - Validation functions to combine
 * @returns {function} - A function that returns true only if all validators return true
 */
export const combineValidators = (...fns) => {
  return (value) => fns.every(fn => fn(value));
};

/**
 * Validation schemas for different form types
 */
export const validationSchemas = {
  // Standard quote form validation schema
  standardQuote: {
    name: { 
      validate: (value) => isNotEmpty(value),
      message: 'First name is required' 
    },
    lastName: { 
      validate: (value) => isNotEmpty(value),
      message: 'Last name is required' 
    },
    email: { 
      validate: (value) => isValidEmail(value),
      message: 'Please enter a valid email address' 
    },
    phone: { 
      validate: (value) => isValidPhone(value),
      message: 'Please enter a valid phone number' 
    },
    propertyAddress: { 
      validate: (value) => isNotEmpty(value),
      message: 'Property address is required' 
    },
    insured: { 
      validate: (value) => isNotEmpty(value),
      message: 'Please select whether you are currently insured' 
    }
  },
  
  // Guardian Angel form validation schema
  guardianAngel: {
    firstName: { 
      validate: (value) => isNotEmpty(value),
      message: 'First name is required' 
    },
    lastName: { 
      validate: (value) => isNotEmpty(value),
      message: 'Last name is required' 
    },
    email: { 
      validate: (value) => isValidEmail(value),
      message: 'Please enter a valid email address' 
    },
    phone: { 
      validate: (value) => isValidPhone(value),
      message: 'Please enter a valid phone number' 
    }
  }
};

/**
 * Validates an entire form based on a schema
 * @param {object} formData - The form data to validate
 * @param {object} schema - The validation schema
 * @returns {object} - An object with errors and isValid properties
 */
export const validateForm = (formData, schema) => {
  const errors = {};
  let isValid = true;
  
  // Define product types for which propertyAddress is required
  const propertyAddressRequiredFor = ['Home Insurance', 'Auto Insurance', 'Flood Insurance', 'Umbrella Insurance'];

  for (const field in schema) {
    // Special handling for propertyAddress based on productType
    if (field === 'propertyAddress') {
      if (!propertyAddressRequiredFor.includes(formData.productType)) {
        continue; // Skip validation for propertyAddress if not required for this productType
      }
    }

    if (Object.prototype.hasOwnProperty.call(formData, field) || schema[field].validateAlways) { // Ensure field is part of form or schema dictates it must be validated
      const { validate, message } = schema[field];
      const valueToValidate = formData[field]; // Could be an empty string, null, or undefined if not touched

      // If the field is not required (e.g. propertyAddress for certain product types)
      // and it's empty, it should not fail validation.
      // The `isNotEmpty` validator itself handles "required" aspect.
      // The main change is the conditional skip for propertyAddress.
      
      const isFieldValid = validate(valueToValidate);
      
      if (!isFieldValid) {
        errors[field] = message;
        isValid = false;
      }
    }
  }
  
  return { errors, isValid };
};

export default {
  isNotEmpty,
  isValidEmail,
  isValidPhone,
  validateField,
  combineValidators,
  validationSchemas,
  validateForm
}; 